import React, { useState, useRef, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence } from "framer-motion";
import BattleHeader from "./BattleHeader";
import { BettingZoneCanvas } from "./BettingZone";
import NarrativeSection from "./NarrativeSection";
import type { Order } from "./BettingZone";
import { useNarrative } from "../../hooks/useNarrative";
import { useTournament } from "../../contexts/TournamentContext";
import { useBetQueue } from "../../hooks/useBetQueue";
import { useTheme } from "../../contexts/ThemeContext";
import { useSoundEffect } from "../../hooks/useSound";
import { SOUNDS } from "../../utils/soundManager";
import { BATTLE_TIMINGS, BATTLE_VALUES } from "../../constants/battleTimings";
import {
  screenVariants,
  coinVariants,
  floatingTextVariants,
} from "./animations";
import {
  VictoryPhase,
  CoinParticle,
  FloatingText,
  MatchScreenProps,
} from "./types";

const MatchScreen: React.FC<MatchScreenProps> = ({ scriptedWinner = "B" }) => {
  const narrativeContext = useNarrative();
  const { tournament } = useTournament();
  const { colors } = useTheme();
  const playVictorySound = useSoundEffect(SOUNDS.VICTORY);
  const playCoinSound = useSoundEffect(SOUNDS.COIN);
  const playChargeupSound = useSoundEffect(SOUNDS.CHARGEUP);
  const playBlastedSound = useSoundEffect(SOUNDS.BLASTED);
  const playWinnerSound = useSoundEffect(SOUNDS.WINNER);

  // Get tokens from tournament context
  const finalsMatch = tournament.rounds[0]?.matches[0];
  const tokenA = finalsMatch?.tokenA;
  const tokenB = finalsMatch?.tokenB;

  const [meterPosition, setMeterPosition] = useState(50);
  const [orders, setOrders] = useState<Order[]>([]);
  const [victoryPhase, setVictoryPhase] = useState<VictoryPhase>("idle");

  // Battle state - local to this component for animation purposes
  // Initialized from tournament tokens but managed locally during battle
  const [battleLiquidityA, setBattleLiquidityA] = useState(
    tokenA?.liquidity || 0
  );
  const [battleLiquidityB, setBattleLiquidityB] = useState(
    tokenB?.liquidity || 0
  );
  const [transferProgress, setTransferProgress] = useState(0); // 0 to 1 during transfer
  const [coins, setCoins] = useState<CoinParticle[]>([]);
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const [screenShake, setScreenShake] = useState(0);
  const bettingZoneRef = useRef<HTMLDivElement>(null);
  const tokenARef = useRef<HTMLDivElement>(null);
  const tokenBRef = useRef<HTMLDivElement>(null);
  const simulatedBetIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const coinSoundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle bet execution from the queue
  const handleBetExecution = useCallback(
    (bet: {
      id: string;
      side: "left" | "right";
      amount: number;
      isUserBet: boolean;
    }) => {
      const newOrder: Order = {
        side: bet.side,
        amount: bet.amount,
        id: bet.id,
        isUserBet: bet.isUserBet,
      };

      setOrders((prev) => [...prev, newOrder]);

      // Add bet amount to the battle liquidity tracking
      if (bet.side === "left") {
        setBattleLiquidityA((prev) => prev + bet.amount);
      } else {
        setBattleLiquidityB((prev) => prev + bet.amount);
      }

      // Remove order after animation
      setTimeout(() => {
        setOrders((prev) => prev.filter((o) => o.id !== newOrder.id));
      }, BATTLE_TIMINGS.ORDERS.DISPLAY_DURATION);

      // If user bet on the losing side, queue a counter-bet for the winner
      if (bet.isUserBet) {
        const winnerSide = scriptedWinner === "A" ? "left" : "right";
        if (bet.side !== winnerSide) {
          // Counter bet is slightly larger than user bet (10-30% more)
          const counterAmount = Math.min(
            BATTLE_VALUES.BETTING.MAX_BET,
            Math.round(
              bet.amount *
                (BATTLE_VALUES.BETTING.COUNTER_BET_MULTIPLIER_MIN +
                  Math.random() *
                    (BATTLE_VALUES.BETTING.COUNTER_BET_MULTIPLIER_MAX -
                      BATTLE_VALUES.BETTING.COUNTER_BET_MULTIPLIER_MIN))
            )
          );
          const delay =
            Math.random() *
              (BATTLE_TIMINGS.BETTING.COUNTER_BET_MAX_DELAY -
                BATTLE_TIMINGS.BETTING.COUNTER_BET_MIN_DELAY) +
            BATTLE_TIMINGS.BETTING.COUNTER_BET_MIN_DELAY;
          queueBet("counter", winnerSide, counterAmount, delay);
        }
      }
    },
    [scriptedWinner]
  );

  // Initialize bet queue
  const { queueBet, clearQueue } = useBetQueue({
    onBetExecute: handleBetExecution,
    isActive: victoryPhase === "idle",
  });

  // Handle screen shake
  const handleScreenShake = useCallback((intensity: number) => {
    setScreenShake(intensity);
    setTimeout(() => setScreenShake(0), 200);
  }, []);

  // Handle narrative key changes
  useEffect(() => {
    if (!tokenA || !tokenB) return;
    switch (narrativeContext.currentKey) {
      case "finals.start":
        // Give the loser a thin lead at the start
        if (scriptedWinner === "A") {
          // B (loser) has the lead, so meter favors B (right side)
          setMeterPosition(BATTLE_VALUES.METER.LOSER_ADVANTAGE_A_WINS);
        } else {
          // A (loser) has the lead, so meter favors A (left side)
          setMeterPosition(BATTLE_VALUES.METER.LOSER_ADVANTAGE_B_WINS);
        }
        break;

      case "finals.victory":
        // Clear bet queue as soon as we hit victory
        clearQueue();
        performVictory();
        break;
    }
  }, [narrativeContext.currentKey, scriptedWinner]);

  // Handle victory sequence phases
  useEffect(() => {
    if (!tokenA || !tokenB) return;
    if (victoryPhase === "idle") return;

    const runPhase = async () => {
      switch (victoryPhase) {
        case "meter-overdrive":
          // Build up tension with chargeup sound
          playChargeupSound(0.7);

          const targetPosition =
            scriptedWinner === "A"
              ? BATTLE_VALUES.METER.VICTORY_POSITION_A
              : BATTLE_VALUES.METER.VICTORY_POSITION_B;

          // Snap at the end of the overdrive phase
          setTimeout(() => {
            setMeterPosition(targetPosition);
            setVictoryPhase("elimination");
          }, BATTLE_TIMINGS.VICTORY.METER_OVERDRIVE_DURATION);
          break;

        case "elimination":
          // Play blasted sound for the elimination
          playBlastedSound(0.8);
          // Also play victory sound slightly after
          setTimeout(() => playVictorySound(0.6), 200);

          // Elimination happens in TokenCard component
          // Wait for elimination animation to complete
          setTimeout(
            () => setVictoryPhase("transfer"),
            BATTLE_TIMINGS.VICTORY.ELIMINATION_WAIT
          );
          break;

        case "transfer":
          // Reset transfer progress for animation
          setTransferProgress(0);

          // Generate coin particles
          const loserRef = scriptedWinner === "A" ? tokenBRef : tokenARef;
          const winnerRef = scriptedWinner === "A" ? tokenARef : tokenBRef;

          if (loserRef.current && winnerRef.current) {
            const loserRect = loserRef.current.getBoundingClientRect();
            const winnerRect = winnerRef.current.getBoundingClientRect();

            // Create LOTS of varied coins with different sizes and delays
            const coinSizes = BATTLE_VALUES.COINS.SIZES;
            const coinValues = BATTLE_VALUES.COINS.VALUES;

            const newCoins = Array.from(
              { length: BATTLE_VALUES.COINS.TOTAL_PARTICLES },
              (_, i) => ({
                id: uuidv4(),
                startX:
                  loserRect.left +
                  loserRect.width / 2 +
                  (Math.random() - 0.5) * BATTLE_VALUES.COINS.POSITION_VARIANCE,
                startY:
                  loserRect.top +
                  loserRect.height / 2 +
                  (Math.random() - 0.5) * BATTLE_VALUES.COINS.POSITION_VARIANCE,
                endX: winnerRect.left + winnerRect.width / 2,
                endY: winnerRect.top + winnerRect.height / 2,
                size: coinSizes[Math.floor(Math.random() * coinSizes.length)],
                value:
                  coinValues[Math.floor(Math.random() * coinValues.length)],
                delay:
                  i * BATTLE_TIMINGS.COINS.PARTICLE_DELAY_BASE +
                  Math.random() * BATTLE_TIMINGS.COINS.PARTICLE_DELAY_VARIANCE,
              })
            );
            setCoins(newCoins);

            // Add floating text for major transfers
            const floatingText: FloatingText = {
              id: uuidv4(),
              x: winnerRect.left + winnerRect.width / 2,
              y: winnerRect.top + winnerRect.height / 2,
              value: `+$${(scriptedWinner === "A"
                ? battleLiquidityB
                : battleLiquidityA
              ).toLocaleString()}`,
            };
            setFloatingTexts([floatingText]);
            setTimeout(
              () => setFloatingTexts([]),
              BATTLE_TIMINGS.VICTORY.FLOATING_TEXT_DURATION
            );
          }

          // Animate transfer progress
          const startTime = Date.now();
          const duration = BATTLE_TIMINGS.VICTORY.TRANSFER_DURATION;

          // Play coin sound repeatedly during transfer at 80% volume
          coinSoundIntervalRef.current = setInterval(() => {
            playCoinSound(0.4);
          }, 50); // Play every 50ms for rapid coin sounds

          const animateTransfer = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            setTransferProgress(eased);

            if (progress < 1) {
              requestAnimationFrame(animateTransfer);
            } else {
              // Stop coin sounds when transfer completes
              if (coinSoundIntervalRef.current) {
                clearInterval(coinSoundIntervalRef.current);
                coinSoundIntervalRef.current = null;
              }
              
              // Play winner sound when liquidity transfer completes
              playWinnerSound(0.5);
              
              // Update actual liquidity values at the end
              const loserLiquidity =
                scriptedWinner === "A" ? battleLiquidityB : battleLiquidityA;
              let finalLiquidityA: number;
              let finalLiquidityB: number;

              if (scriptedWinner === "A") {
                finalLiquidityA = battleLiquidityA + loserLiquidity;
                finalLiquidityB = 0;
                setBattleLiquidityA(finalLiquidityA);
                setBattleLiquidityB(finalLiquidityB);
              } else {
                finalLiquidityA = 0;
                finalLiquidityB = battleLiquidityB + loserLiquidity;
                setBattleLiquidityA(finalLiquidityA);
                setBattleLiquidityB(finalLiquidityB);
              }

              setVictoryPhase("complete");
              // Clear coins after animation
              setTimeout(
                () => setCoins([]),
                BATTLE_TIMINGS.VICTORY.COINS_CLEANUP_DELAY
              );
            }
          };

          requestAnimationFrame(animateTransfer);
          break;

        case "complete":
          // Victory complete
          break;
      }
    };

    runPhase();

    // Cleanup function for coin sound interval
    return () => {
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current);
        coinSoundIntervalRef.current = null;
      }
    };
  }, [
    victoryPhase,
    scriptedWinner,
    battleLiquidityA,
    battleLiquidityB,
    tokenA,
    tokenB,
    playCoinSound,
    playChargeupSound,
    playBlastedSound,
    playVictorySound,
    playWinnerSound,
  ]);

  // Calculate displayed liquidity values during transfer
  const getDisplayLiquidity = (side: "A" | "B"): number => {
    if (victoryPhase !== "transfer") {
      return side === "A" ? battleLiquidityA : battleLiquidityB;
    }

    const isWinner =
      (side === "A" && scriptedWinner === "A") ||
      (side === "B" && scriptedWinner === "B");
    const isLoser = !isWinner;
    const loserLiquidity =
      scriptedWinner === "A" ? battleLiquidityB : battleLiquidityA;
    const winnerLiquidity =
      scriptedWinner === "A" ? battleLiquidityA : battleLiquidityB;

    if (isLoser) {
      // Loser's liquidity drains to 0
      return Math.round(loserLiquidity * (1 - transferProgress));
    } else {
      // Winner's liquidity increases
      return Math.round(winnerLiquidity + loserLiquidity * transferProgress);
    }
  };

  // Update meter position based on liquidity ratio
  useEffect(() => {
    // Don't update meter position during victory sequence
    if (victoryPhase !== "idle") return;

    const totalLiquidity = battleLiquidityA + battleLiquidityB;
    if (totalLiquidity > 0) {
      const newPosition = (battleLiquidityA / totalLiquidity) * 100;
      setMeterPosition(newPosition);
    }
  }, [battleLiquidityA, battleLiquidityB, victoryPhase]);

  // Simulate other users placing bets during battle
  useEffect(() => {
    // Clear any existing interval
    if (simulatedBetIntervalRef.current) {
      clearInterval(simulatedBetIntervalRef.current);
      simulatedBetIntervalRef.current = null;
    }

    // Stop bets during victory
    if (victoryPhase !== "idle") {
      return;
    }

    // Determine betting intensity based on narrative phase
    const isRally = narrativeContext.currentKey === "finals.rally";
    const baseInterval = isRally
      ? BATTLE_TIMINGS.BETTING.RALLY_INTERVAL
      : BATTLE_TIMINGS.BETTING.NORMAL_INTERVAL;
    const intervalVariance = isRally
      ? BATTLE_TIMINGS.BETTING.RALLY_VARIANCE
      : BATTLE_TIMINGS.BETTING.NORMAL_VARIANCE;

    const scheduleNextBet = () => {
      const winnerBias = isRally
        ? BATTLE_VALUES.PROBABILITIES.WINNER_BIAS_RALLY
        : BATTLE_VALUES.PROBABILITIES.WINNER_BIAS_NORMAL;
      const winnerSide = scriptedWinner === "A" ? "left" : "right";
      const side =
        Math.random() < winnerBias
          ? winnerSide
          : winnerSide === "left"
          ? "right"
          : "left";

      // Bigger bets during rally, scaled to new range ($10-$500)
      const amount = isRally
        ? Math.floor(
            Math.random() *
              (BATTLE_VALUES.BETTING.RALLY_MAX -
                BATTLE_VALUES.BETTING.RALLY_MIN)
          ) + BATTLE_VALUES.BETTING.RALLY_MIN
        : Math.floor(
            Math.random() *
              (BATTLE_VALUES.BETTING.NORMAL_MAX -
                BATTLE_VALUES.BETTING.NORMAL_MIN)
          ) + BATTLE_VALUES.BETTING.NORMAL_MIN;

      // Queue the simulated bet with a small random delay
      const delay = Math.random() * 100; // 0-100ms variation
      queueBet("simulated", side, amount, delay);
    };

    simulatedBetIntervalRef.current = setInterval(() => {
      scheduleNextBet();
    }, Math.random() * intervalVariance + baseInterval);

    // Schedule first bet immediately
    scheduleNextBet();

    return () => {
      if (simulatedBetIntervalRef.current) {
        clearInterval(simulatedBetIntervalRef.current);
        simulatedBetIntervalRef.current = null;
      }
    };
  }, [scriptedWinner, narrativeContext.currentKey, victoryPhase, queueBet]);

  const handleBet = (side: "left" | "right", amount: number) => {
    queueBet("user", side, amount, 0); // User bets execute immediately
  };

  const performVictory = () => {
    // Clear any pending bet intervals
    if (simulatedBetIntervalRef.current) {
      clearInterval(simulatedBetIntervalRef.current);
      simulatedBetIntervalRef.current = null;
    }

    // Clear the bet queue
    clearQueue();

    // Clear any pending order animations
    setOrders([]);

    setVictoryPhase("meter-overdrive");
  };

  // Early return if tokens not available
  if (!tokenA || !tokenB) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white">
        Loading battle...
      </div>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black flex flex-col battle-screen"
      style={{
        transform:
          screenShake > 0
            ? `translate(${(Math.random() - 0.5) * screenShake * 10}px, ${
                (Math.random() - 0.5) * screenShake * 10
              }px)`
            : undefined,
        transition: screenShake > 0 ? "transform 50ms" : undefined,
      }}
      variants={screenVariants}
      initial="idle"
      animate={victoryPhase === "meter-overdrive" ? "meterOverdrive" : "idle"}
    >
      <BattleHeader
        tokenA={tokenA}
        tokenB={tokenB}
        meterPosition={meterPosition}
        victoryPhase={victoryPhase}
        scriptedWinner={scriptedWinner}
        getDisplayLiquidity={getDisplayLiquidity}
        tokenARef={tokenARef}
        tokenBRef={tokenBRef}
      />

      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex relative overflow-hidden" ref={bettingZoneRef}>
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="h-full w-full"
              style={{
                backgroundImage: `linear-gradient(${colors.grid.lineColor} 1px, transparent 1px), linear-gradient(90deg, ${colors.grid.lineColor} 1px, transparent 1px)`,
                backgroundSize: "50% 25%",
                opacity: colors.grid.lineOpacity,
              }}
            />
            <div
              className={`absolute top-0 left-0 right-0 h-px ${colors.grid.borderMain}`}
            />
            <div
              className={`absolute left-0 right-0 h-px ${colors.grid.borderLight}`}
              style={{ top: "25%" }}
            />
            <div
              className={`absolute left-0 right-0 h-px ${colors.grid.borderLight}`}
              style={{ top: "50%" }}
            />
            <div
              className={`absolute left-0 right-0 h-px ${colors.grid.borderLight}`}
              style={{ top: "75%" }}
            />
            <div
              className={`absolute bottom-0 left-0 right-0 h-px ${colors.grid.borderMain}`}
            />
            <div
              className={`absolute top-0 bottom-0 w-px ${colors.grid.centerLine}`}
              style={{ left: "50%" }}
            />
          </div>

          <BettingZoneCanvas
            orders={orders}
            onBet={handleBet}
            disabled={narrativeContext?.currentKey?.includes("conclusion")}
            onScreenShake={handleScreenShake}
            victoryPhase={victoryPhase}
            winner={scriptedWinner}
            transferProgress={transferProgress}
            themeColors={colors}
          />
        </div>
        
        <NarrativeSection />
      </div>

      {/* Coin particles with arc trajectories */}
      <AnimatePresence>
        {coins.map((coin) => {
          const sizeClasses = {
            small: "w-6 h-6 text-xs",
            medium: "w-8 h-8 text-sm",
            large: "w-10 h-10 text-base",
          };

          return (
            <motion.div
              key={coin.id}
              className={`fixed ${
                sizeClasses[coin.size]
              } bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full shadow-lg pointer-events-none z-50`}
              style={{
                left: coin.startX,
                top: coin.startY,
                boxShadow:
                  coin.size === "large"
                    ? "0 0 25px rgba(251, 191, 36, 1)"
                    : "0 0 15px rgba(251, 191, 36, 0.8)",
              }}
              custom={coin}
              variants={coinVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div className="w-full h-full flex items-center justify-center font-bold text-yellow-900">
                {coin.size === "large" ? `$${coin.value}` : "$"}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Floating value text */}
      <AnimatePresence>
        {floatingTexts.map((text) => (
          <motion.div
            key={text.id}
            className="fixed text-2xl font-black text-green-400 pointer-events-none z-50"
            style={{
              left: text.x,
              top: text.y,
              textShadow: "0 0 20px rgba(34, 197, 94, 0.8)",
            }}
            variants={floatingTextVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {text.value}
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
};

export default MatchScreen;
